import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import {
  createInvoiceIncomingPaymentAPI,
  deleteInvoiceIncomingPaymentAPI,
} from "services/api/finance/incoming-invoices/api";
import { getPaymentMethodsAPI } from "services/api/finance/lookups/api";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { Button } from "ui/components/Button/Button";
import { TableSearch } from "ui/components/Table/TableSearch";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { NumberField } from "ui/components/TextField/NumberField";
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";
import { Dialog } from "ui/components/Dialog";
import { INCOMING_INVOICE_PAYMENTS_COLUMNS } from "./consts";
import { InvoiceIncomingPaymentsProps } from "./types";
import {
  useHandleNumberFieldChange,
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

interface PaymentFormValues {
  amount: number | null;
  paidAt: Date | null;
  paymentMethodId: string | null;
  paymentReference: string | null;
  note: string | null;
}

const initialPaymentForm: PaymentFormValues = {
  amount: null,
  paidAt: new Date(),
  paymentMethodId: null,
  paymentReference: null,
  note: null,
};

const InvoiceIncomingPayments: React.FC<InvoiceIncomingPaymentsProps> = ({ invoiceId, payments, remainingAmount, onPaymentChange, showModal, setShowModal }) => {
  const { t } = useTranslate();
  const [formValues, setFormValues] = useState<PaymentFormValues>(initialPaymentForm);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<DropdownOption[]>([]);
  const [deleteId, setDeleteId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchLookups();
  }, []);

  const fetchLookups = async () => {
    try {
      const methods = await getPaymentMethodsAPI();
      setPaymentMethodOptions(methods?.map((m: any) => ({ text: m.name, value: m.id })) ?? []);
    } catch {}
  };

  // Kad se modal otvara (bilo gumbom ili statusbarom), postavi default iznos i metodu
  useEffect(() => {
    if (showModal) {
      setFormValues({
        ...initialPaymentForm,
        amount: remainingAmount > 0 ? remainingAmount : null,
        paymentMethodId: "transakcijski",
      });
    }
  }, [showModal, remainingAmount]);

  const handleAddClick = () => {
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formValues.amount || formValues.amount <= 0) return;

    setIsLoading(true);
    try {
      await createInvoiceIncomingPaymentAPI(
        invoiceId,
        {
          amount: formValues.amount,
          paidAt: formValues.paidAt ?? new Date(),
          paymentMethodId: formValues.paymentMethodId,
          paymentReference: formValues.paymentReference,
          note: formValues.note,
        },
        t
      );
      setShowModal(false);
      onPaymentChange();
    } catch {}
    setIsLoading(false);
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteId(id);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteId) return;
    try {
      await deleteInvoiceIncomingPaymentAPI(invoiceId, deleteId, t);
      onPaymentChange();
    } catch {}
    setOpenDialog(false);
    setDeleteId("");
  }, [deleteId, invoiceId, t, onPaymentChange]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteId("");
    setOpenDialog(false);
  }, []);

  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  return (
    <>
      <div className="mt-4">
        <div className="flex items-center justify-end mb-2">
         
          <Button
            variant="primary"
            children="Dodaj uplatu"
            startIcon="/media/icons/plus.svg"
            onClick={handleAddClick}
          />
        </div>

        {payments.length > 0 && (
          <div className="">
       
              <TableSearch
                columns={INCOMING_INVOICE_PAYMENTS_COLUMNS(t, handleDeleteClick)}
                data={payments}
                isLoading={false}
                selectableRows={false}
              />
  
          </div>
        )}
      </div>

      {/* Modal za dodavanje uplate */}
      <Modal show={showModal} onHide={() => setShowModal(false)} position="centered" size="small">
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title="Dodaj uplatu"
          onHide={() => setShowModal(false)}
        />
        <ModalBody>
          <div className="px-5 pt-3 pb-5 space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <NumberField
                  name="amount"
                  label="Iznos"
                  value={formValues.amount}
                  onChange={handleNumberFieldChange}
                  decimals={2}
                  required
                />
              </div>
              <div className="flex-1">
                <DatePicker
                  name="paidAt"
                  label="Datum uplate"
                  value={formValues.paidAt}
                  onChange={handleDateChange}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <Dropdown
                  name="paymentMethodId"
                  label="Metoda plaćanja"
                  options={paymentMethodOptions}
                  value={formValues.paymentMethodId}
                  onChange={handleDropdownChange}
                  placeholder="Odaberi metodu"
                />
              </div>
              <div className="flex-1">
                <TextField
                  type="text"
                  name="paymentReference"
                  label="Poziv na broj"
                  value={formValues.paymentReference}
                  onChange={handleTextFieldChange}
                />
              </div>
            </div>

            <TextArea
              name="note"
              label="Napomena"
              value={formValues.note}
              onChange={handleTextFieldChange}
            />
          </div>

        </ModalBody>
        <ModalFooter>
          <div className="px-4 py-3 flex justify-end gap-3">
            <Button variant="outlined" children="Zatvori" onClick={() => setShowModal(false)} />
            <Button variant="primary" children="Spremi uplatu" onClick={handleSubmit} />
          </div>
        </ModalFooter>
      </Modal>

      <Dialog
        open={openDialog}
        title="Brisanje uplate"
        message="Jeste li sigurni da želite obrisati ovu uplatu?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default memo(InvoiceIncomingPayments);
